import {Box, Stack, Typography} from '@mui/material';
import {FeaturesEnum, HomeEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import React, {useRef, useState} from 'react';
import {AlertActions, AlertInfo, AlertModal} from 'presentation/components';
import {FormProvider} from 'react-hook-form';
import {useNavigate, useLocation} from 'react-router-dom';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {PredefinedFavoritePlacesEnum} from 'domain/usecases';
import {useAddressFormV2} from '../hooks/useAddressFormV2';
import {AddressAlerts} from './components/AddressAlerts';
import {AddressFields} from './components/AddressFields';
import {AddressFooter} from './components/AddressFooter';
import {AddressIdentification} from './components/AddressIdentification';
import {AddressSearchType} from './components/AddressSearchType';
import {AddressSearchTypeSelector} from './components/AddressSearchTypeSelector';
import {AddressSearchMode} from './components/AddressSearchTypeSelector/types';
import {AddressFormV2Props} from './types';
import {CTAButtonFixed} from 'presentation/components/CTAButtonFixed';
import {Loading} from 'presentation/components/Loading/Loading';
import {useMediaQueries} from 'presentation/pages';
import {TutorialStepsId} from 'presentation/pages/private/profile/FavoriteAddresses/components/FavoriteAddressTutorials/constants';
import {Instructions} from 'presentation/components/Location/Instructions';
import {InstabilityError} from 'presentation/components/Location/InstabilityError';
import {FavoriteAddressesAddRouteState} from 'presentation/pages/private/profile/FavoriteAddresses/hooks/types';

const {save} = translations['pt-br'].commons;
const {title} = translations['pt-br'].privateFlow.profile.changeData.address;
const {mainAddressConfirmationModal, addressInfoAlertDescription} =
	translations['pt-br'].privateFlow.profile.changeData.addresses;

export function AddressFormV2({
	onCancel,
	onSave,
	loading = false,
	initialReverseGeocodeData,
	address,
	withoutAlertInfo = false,
	withoutCancelButton = false,
	responsiveButtons = false,
	hasFavoriteAddressField = false,
	forceMainAddress = false,
	favoriteAddressDescription,
	saveLabel = save,
	hasCasaAddress = false,
	isEditingCasaAddress = false,
	showAddressInfoAlert = false,
	enableCepNotFoundError = false,
}: AddressFormV2Props) {
	const {isTablet} = useMediaQueries();
	const navigate = useNavigate();
	const {state: routeState} = useLocation();
	const favoriteAddressesRouteState = routeState as FavoriteAddressesAddRouteState | null;

	const [openMainAddressConfirmation, setOpenMainAddressConfirmation] = useState(false);
	const previousFavoriteAddressRef = useRef<string | undefined>(undefined);
	const {isEnabled: isGeolocEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_MULTI_ADDRESS_GEOLOC,
	);

	const {
		methods,
		showOtherFields,
		operationState,
		fieldDisabledState,
		warningInfo,
		unmaskedZipCode,
		watchWithoutNumber,
		showAlert,
		typeMessage,
		handleBack,
		handleCheckbox,
		handleChange,
		isMainAddress,
		searchMode,
		handleSearchModeChange,
		handleZipCodeFocus,
		geolocation,
	} = useAddressFormV2({
		onCancel,
		address,
		initialReverseGeocodeData,
		hasFavoriteAddressField,
		forceMainAddress,
		favoriteAddressDescription,
		enableCepNotFoundError,
	});

	const restrictToCasaOnly =
		!hasCasaAddress || isEditingCasaAddress || favoriteAddressesRouteState?.isCasaDeletion;
	const showCasaConfirmationOnSelect =
		hasCasaAddress && !isEditingCasaAddress && !favoriteAddressesRouteState?.isCasaDeletion;

	const handleCasaSelected = (previousFavoriteAddress?: string) => {
		if (!showCasaConfirmationOnSelect) return;
		previousFavoriteAddressRef.current = previousFavoriteAddress;
		setOpenMainAddressConfirmation(true);
	};

	const handleConfirmMainAddress = () => {
		setOpenMainAddressConfirmation(false);
	};

	const handleCloseMainAddressConfirmation = () => {
		methods.setValue(
			'favoriteAddress',
			previousFavoriteAddressRef.current ?? PredefinedFavoritePlacesEnum.OTHER,
		);
		setOpenMainAddressConfirmation(false);
	};

	const handleSave = methods.handleSubmit(values =>
		onSave({
			...values,
			type: address?.type ?? undefined,
			isMainAddress: hasFavoriteAddressField
				? values.favoriteAddress === PredefinedFavoritePlacesEnum.HOUSE
				: isMainAddress,
		}),
	);

	const showAddressSearchType =
		searchMode === AddressSearchMode.ZIP_CODE ||
		(searchMode === AddressSearchMode.CURRENT_LOCATION && showOtherFields);
	return (
		<>
			<FormProvider {...methods}>
				<Stack
					id={TutorialStepsId.ADDRESS_SEARCH}
					gap={2}
					sx={{backgroundColor: 'background.paper', padding: 2, borderRadius: 1}}>
					<Typography
						variant="body1"
						color="text.primary"
						fontSize="1.125rem"
						fontWeight={600}>
						{title}
					</Typography>

					<AddressAlerts
						showAlert={showAlert}
						warningInfo={warningInfo}
						typeMessage={typeMessage}
						withoutAlertInfo={withoutAlertInfo}
					/>

					{isGeolocEnabled ? (
						<AddressSearchTypeSelector
							selectedMode={searchMode}
							onModeChange={handleSearchModeChange}
							isLocating={geolocation.isLocating}
							isLocationBlocked={geolocation.isLocationBlocked}
						/>
					) : null}

					{showAddressSearchType ? (
						<AddressSearchType
							operationState={operationState}
							onZipCodeChange={handleChange}
							onZipCodeFocus={handleZipCodeFocus}
						/>
					) : null}

					<AddressFields
						showOtherFields={showOtherFields}
						unmaskedZipCode={unmaskedZipCode}
						fieldDisabledState={fieldDisabledState}
						watchWithoutNumber={watchWithoutNumber}
						onWithoutNumberChange={handleCheckbox}
					/>
				</Stack>
				{hasFavoriteAddressField ? (
					<AddressIdentification
						restrictToCasaOnly={restrictToCasaOnly}
						onCasaSelected={
							showCasaConfirmationOnSelect ? handleCasaSelected : undefined
						}
					/>
				) : null}
				{showAddressInfoAlert ? (
					<AlertInfo.Root severity="info">
						<AlertInfo.Description>{addressInfoAlertDescription}</AlertInfo.Description>
					</AlertInfo.Root>
				) : null}
				<Stack gap={2}>
					<AddressFooter
						loading={loading}
						withoutCancelButton={withoutCancelButton}
						responsiveButtons={responsiveButtons}
						onBack={handleBack}>
						<CTAButtonFixed.Root $isTablet={isTablet}>
							<CTAButtonFixed.Action
								fullWidth={isTablet}
								onClick={handleSave}
								size="large"
								sx={{alignSelf: 'flex-end', padding: '8px 22px'}}
								endIcon={
									loading ? (
										<Box width={24}>
											<Loading color="inherit" size={14} />
										</Box>
									) : null
								}>
								{saveLabel}
							</CTAButtonFixed.Action>
						</CTAButtonFixed.Root>
					</AddressFooter>
				</Stack>
			</FormProvider>

			<AlertModal
				headerTitle={mainAddressConfirmationModal.headerTitle}
				title={mainAddressConfirmationModal.title}
				variant="info"
				description={mainAddressConfirmationModal.description}
				open={openMainAddressConfirmation}
				onClose={handleCloseMainAddressConfirmation}
				footerContent={
					<AlertActions
						primaryText={mainAddressConfirmationModal.primaryAction}
						secondaryText={mainAddressConfirmationModal.secondaryAction}
						primaryAction={handleConfirmMainAddress}
						secondaryAction={handleCloseMainAddressConfirmation}
						flexDirection="column-reverse"
					/>
				}
			/>
			<Instructions
				open={geolocation.openInstructions}
				onOpen={() => {}}
				onClose={() => {
					geolocation.closeInstructions();
					handleSearchModeChange(AddressSearchMode.ZIP_CODE);
				}}
				onManualSelection={() => {
					geolocation.closeInstructions();
					handleSearchModeChange(AddressSearchMode.ZIP_CODE);
				}}
			/>

			<InstabilityError
				open={geolocation.openInstabilityError}
				positionUnavailableOrTimeout={geolocation.positionUnavailableOrTimeout}
				geolocationOrPermissionIsNotSupported={
					geolocation.geolocationOrPermissionIsNotSupported
				}
				isUnprocessableError={geolocation.locationErrorKind === 'apiError'}
				onClose={() => {
					geolocation.closeInstabilityError();
					handleSearchModeChange(AddressSearchMode.ZIP_CODE);
				}}
				onGoHome={() => {
					geolocation.closeInstabilityError();
					navigate(HomeEnum.HOME_PAGE);
				}}
				onManualSelection={() => {
					geolocation.closeInstabilityError();
					handleSearchModeChange(AddressSearchMode.ZIP_CODE);
				}}
				onRetry={geolocation.retryLocation}
			/>
		</>
	);
}
